// ══════════════════════════════════════════════════════════════════════════════
//  src/components/teacher/useProfesor.js
//  LOS DATOS DEL PORTAL DEL PROFESOR
//
//  ── UNA COSA QUE NO VERAS EN NINGUNA LLAMADA DE ESTE FICHERO ────────────────
//  Un `teacher_id`. Ninguna ruta de `/api/teacher` lo acepta: el profesor sale
//  del token. Si algun dia alguien anade aqui un `?teacher_id=`, el servidor lo
//  ignorara — y conviene que lo sepa antes de perder una tarde buscando por que
//  no le hace caso.
// ══════════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motivoDelFallo } from '../../limites.js';

const API = '/api/teacher';

export const CLAVES = {
  yo:      ['profesor', 'me'],
  agenda:  (mes) => ['profesor', 'agenda', mes || 'todo'],
  alumnos: ['profesor', 'alumnos'],
  historial: (id) => ['profesor', 'historial', id],
  ganancias: ['profesor', 'ganancias'],
  contrato: ['profesor', 'contrato'],
  empresas: (mes) => ['profesor', 'empresas', mes || 'todo'],
  claseEmpresa: (id) => ['profesor', 'empresas', 'clase', id],
};

/**
 * Una llamada al portal, con el fallo ya traducido a algo accionable.
 *
 * El `motivoDelFallo` de `limites.js` existe porque un 422 contado como
 * «problema de conexion» manda a mirar el wifi cuando lo que sobra es texto.
 * El profesor merece el mismo trato que el alumno: aqui las cajas de texto
 * tambien tienen tope, y tambien pueden devolver un 422.
 */
async function llama(ruta, { metodo = 'GET', cuerpo } = {}) {
  const token = localStorage.getItem('token');
  const r = await fetch(API + ruta, {
    method: metodo,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(cuerpo ? { 'Content-Type': 'application/json' } : {}),
    },
    body: cuerpo ? JSON.stringify(cuerpo) : undefined,
  });

  if (!r.ok) {
    let detalle = null;
    try {
      const j = await r.json();
      detalle = typeof j?.detail === 'string' ? j.detail : null;
    } catch {
      detalle = null;
    }
    // El servidor casi siempre explica mejor que una tabla generica: dice «esa
    // clase no esta en tu agenda» o «hay que avisar con 24 horas». Solo se cae
    // a la traduccion por codigo cuando no ha dicho nada util.
    const error = new Error(detalle || motivoDelFallo(r.status));
    error.status = r.status;
    throw error;
  }
  return r.json();
}

export function useYo() {
  return useQuery({
    queryKey: CLAVES.yo,
    queryFn: () => llama('/me'),
    staleTime: 60_000,
  });
}

export function useAgenda(mes) {
  return useQuery({
    queryKey: CLAVES.agenda(mes),
    queryFn: () => llama(`/agenda${mes ? `?mes=${mes}` : ''}`),
    staleTime: 30_000,
  });
}

export function useMisAlumnos() {
  return useQuery({
    queryKey: CLAVES.alumnos,
    queryFn: () => llama('/alumnos'),
    staleTime: 60_000,
  });
}

export function useHistorial(alumnoId) {
  return useQuery({
    queryKey: CLAVES.historial(alumnoId),
    queryFn: () => llama(`/alumnos/${alumnoId}/evaluaciones`),
    enabled: Boolean(alumnoId),
  });
}

export function usePosponer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ claseId, nuevaFecha, motivo }) =>
      llama(`/clases/${claseId}/posponer`, {
        metodo: 'POST',
        // `motivo` va fuera si esta vacio: el esquema del servidor rechaza
        // campos que no espera, y una cadena vacia no es un motivo.
        cuerpo: { nueva_fecha: nuevaFecha, ...(motivo ? { motivo } : {}) },
      }),
    onSuccess: () => {
      // La agenda se recarga entera, de todos los meses. Posponer mueve una
      // clase de un mes a otro, asi que refrescar solo el mes que se esta
      // mirando dejaria la clase duplicada en la vista del mes destino.
      qc.invalidateQueries({ queryKey: ['profesor', 'agenda'] });
      qc.invalidateQueries({ queryKey: CLAVES.yo });
    },
  });
}

/* ── SOLTAR UNA CLASE ─────────────────────────────────────────────────────────
   «No puedo dar esta.» No es lo mismo que posponer: posponer es «la doy otro
   dia» y la clase sigue siendo suya; soltarla es «no la doy» y sale de su
   agenda a la espera de que gerencia se la pase a otro profesor.

   El servidor puede contestar 200 SIN haberla soltado —`{soltada: false,
   motivo: "sin_firmar"}`— porque para soltar una clase hay que tener el
   acuerdo firmado: es ahi donde dice lo que cuesta soltarla tarde. Quien use
   este gancho tiene que mirar `soltada`, no solo que no haya error.          */
export function useRenunciarClase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ claseId, motivo }) =>
      llama(`/clases/${claseId}/renunciar`, {
        metodo: 'POST',
        cuerpo: { motivo },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profesor', 'agenda'] });
      // Y la ficha: soltar una clase le cambia el numero de clases en el acto.
      qc.invalidateQueries({ queryKey: CLAVES.yo });
      qc.invalidateQueries({ queryKey: CLAVES.alumnos });
    },
  });
}

export function useEvaluar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alumnoId, ...resto }) =>
      llama(`/alumnos/${alumnoId}/evaluar`, { metodo: 'POST', cuerpo: resto }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: CLAVES.alumnos });
      qc.invalidateQueries({ queryKey: CLAVES.historial(vars.alumnoId) });
    },
  });
}

/* ── MIS GANANCIAS ────────────────────────────────────────────────────────────
   Su codigo de afiliado, sus alumnos referidos y su saldo POR MONEDA.

   Todas las cifras vienen calculadas del servidor. Aqui NO se suma, ni se
   convierte, ni se redondea: sumar pesos con dolares en la pantalla daria una
   cifra que parece correcta y no lo es, y el profesor la leeria como su dinero.
   La pantalla pinta lo que le dan.                                            */
export function useGanancias() {
  return useQuery({
    queryKey: CLAVES.ganancias,
    queryFn: () => llama('/ganancias'),
    staleTime: 30_000,
  });
}

/** Pide cobrar lo acumulado en una moneda.
 *
 *  Solo viaja la MONEDA. El importe lo calcula el servidor desde el libro de
 *  comisiones: si lo mandara la pantalla, cobrar seria escribir un numero.
 *
 *  ⚠️ `metodo` y `cuerpo`, en castellano, que es como los llama `llama`. Estuvo
 *  escrito `method`/`body` —los nombres de `fetch`— y entonces `metodo` se
 *  quedaba en su valor por defecto: la peticion de cobro salia como un GET
 *  contra una ruta POST y moria en un 405. El boton de cobrar no funcionaba y
 *  nada lo decia, porque el fallo estaba en el nombre de un parametro. */
export function usePedirCobro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moneda) => llama('/cobro', {
      metodo: 'POST',
      cuerpo: { moneda },
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.ganancias }),
  });
}

/* ── EL ACUERDO Y EL PRECIO ───────────────────────────────────────────────────
   Las dos cosas que el profesor tiene que hacer una vez: leer y firmar lo que
   acepta, y decir cuanto quiere cobrar por hora.

   El precio que propone NO sale a la vitrina hasta que gerencia lo aprueba, y
   por eso son dos columnas distintas en la base. Aqui se ven las dos: lo que
   pidio y lo que esta publicado. Enseñar solo una de las dos deja al profesor
   creyendo que ya cobra lo que pidio.                                        */

export function useContrato() {
  return useQuery({
    queryKey: CLAVES.contrato,
    queryFn: () => llama('/contrato'),
    staleTime: 60_000,
  });
}

export function useFirmarContrato() {
  const qc = useQueryClient();
  return useMutation({
    // Se manda LA VERSION que se ha leido, no un «acepto». El servidor la
    // compara con la vigente: si el acuerdo cambio mientras estaba abierto,
    // contesta 409 y hay que volver a leerlo.
    mutationFn: (version) => llama('/contrato/firmar', {
      metodo: 'POST',
      cuerpo: { version },
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CLAVES.contrato });
      // El saldo tambien: sin firma no se puede pedir cobro, asi que firmar
      // cambia lo que la pestaña de ganancias tiene que enseñar.
      qc.invalidateQueries({ queryKey: CLAVES.ganancias });
    },
  });
}

/* ── CLASES DE EMPRESA (EMPRESAS.md, Fase 3) ──────────────────────────────────
   Las programa gerencia; aquí el profesor pasa lista y evalúa. Viven en su
   propia tabla, no en la agenda de «Mis clases»: una clase grupal tiene diez
   personas y la agenda de siempre solo sabe de una.

   `pendientes` llega aparte de las del mes: una clase de la semana pasada sin
   reporte tiene que verse aunque se esté mirando otro mes.                    */
export function useClasesEmpresa(mes) {
  return useQuery({
    queryKey: CLAVES.empresas(mes),
    queryFn: () => llama(`/empresas/clases${mes ? `?mes=${mes}` : ''}`),
    staleTime: 30_000,
  });
}

export function useClaseEmpresa(id) {
  return useQuery({
    queryKey: CLAVES.claseEmpresa(id),
    queryFn: () => llama(`/empresas/clases/${id}`),
    enabled: Boolean(id),
    // Siempre fresca al abrirla: si la corrigió en otra pestaña, lo que se
    // enseña tiene que ser lo último que guardó, no lo de hace un minuto.
    staleTime: 0,
  });
}

/* «Entendido» a un cambio que hizo gerencia (19-09-2026). Gerencia lo ve como
   «visto» en el CRM: ya no tiene que escribirle por WhatsApp.               */
export function useCambioVisto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cambioId) => llama(`/empresas/cambios/${cambioId}/visto`, { metodo: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profesor', 'empresas'] }),
  });
}

export function useReporteEmpresa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ claseId, reporte }) =>
      llama(`/empresas/clases/${claseId}/reporte`, { metodo: 'PUT', cuerpo: reporte }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['profesor', 'empresas'] });
      qc.invalidateQueries({ queryKey: CLAVES.claseEmpresa(vars.claseId) });
    },
  });
}

export function useProponerPrecio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (precio) => llama('/precio', {
      metodo: 'POST',
      cuerpo: { precio },
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAVES.yo }),
  });
}
